
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import ReactMarkdown from 'react-markdown'
import { FaArrowLeft, FaCalendar, FaTag } from 'react-icons/fa'

import { useTranslation } from 'react-i18next'

export default function BlogPost() {
    const { t, i18n } = useTranslation()
    const { slug } = useParams()
    const [post, setPost] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        fetchPost()
    }, [slug])

    async function fetchPost() {
        try {
            const { data, error } = await supabase
                .from('posts')
                .select('*')
                .eq('slug', slug)
                .single()

            if (error) throw error
            setPost(data)
        } catch (error) {
            console.error('Error fetching post:', error.message)
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[50vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        )
    }

    if (!post) {
        return (
            <div className="container mx-auto px-4 py-20 text-center">
                <h2 className="text-3xl font-bold mb-4">{t('blog_page.post_not_found')}</h2>
                <Link to="/blog" className="text-primary hover:underline">
                    &larr; {t('blog_page.back_to_blog')}
                </Link>
            </div>
        )
    }

    const isEs = i18n.language === 'es'
    const title = isEs
        ? (post.title_es || post.title_en || post.title || 'Untitled')
        : (post.title_en || post.title || post.title_es || 'Untitled')
    const content = isEs
        ? (post.content_es || post.content_en || post.content || '')
        : (post.content_en || post.content || post.content_es || '')
    const excerpt = isEs
        ? (post.excerpt_es || post.excerpt_en || post.excerpt || '')
        : (post.excerpt_en || post.excerpt || post.excerpt_es || '')
    const tags = post.tags || []

    return (
        <article className="min-h-screen bg-cloud pb-16">
            {/* Hero Image */}
            {post.image_url ? (
                <div className="w-full h-[40vh] md:h-[50vh] relative">
                    <img
                        src={post.image_url}
                        alt={title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-cloud to-transparent opacity-80"></div>
                </div>
            ) : (
                <div className="h-24 md:h-32 bg-cloud"></div>
            )}

            <div className={`container mx-auto px-4 relative z-10 max-w-4xl ${post.image_url ? '-mt-20' : ''}`}>
                <Link
                    to="/blog"
                    className={`inline-flex items-center mb-6 px-4 py-2 rounded-full backdrop-blur-sm transition-colors ${post.image_url ? 'text-white/80 hover:text-white bg-black/50' : 'text-primary hover:text-primary/80 bg-cloud'}`}
                >
                    <FaArrowLeft className="mr-2" /> {t('blog_page.back_to_blog')}
                </Link>

                <div className="bg-white rounded-2xl shadow-xl p-6 md:p-10 border border-gray-200">
                    <header className="mb-8 border-b border-gray-200 pb-8">
                        <h1 className="text-3xl md:text-5xl font-bold mb-4 text-ink leading-tight">
                            {title}
                        </h1>
                        <div className="flex flex-wrap items-center gap-4 text-ash text-sm md:text-base">
                            {post.created_at && (
                                <div className="flex items-center">
                                    <FaCalendar className="mr-2 opacity-70" />
                                    {new Date(post.created_at).toLocaleDateString(i18n.language)}
                                </div>
                            )}
                        </div>
                        {/* Tags */}
                        {tags && tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-4">
                                {tags.map((tag) => (
                                    <Link
                                        key={tag}
                                        to={`/blog?tag=${encodeURIComponent(tag)}`}
                                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium border border-primary/20 hover:bg-primary/20 transition-colors"
                                    >
                                        <FaTag className="text-[10px]" />
                                        {tag}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </header>

                    <div className="prose prose-lg dark:prose-invert max-w-none 
            prose-headings:text-ink prose-p:text-ink 
            prose-a:text-primary prose-strong:text-ink
            prose-li:text-ink prose-ol:text-ink prose-ul:text-ink
            prose-blockquote:text-ash prose-blockquote:border-primary/30
            prose-code:text-ink prose-pre:bg-gray-100 prose-pre:text-ink
            prose-em:text-ink prose-td:text-ink prose-th:text-ink
            prose-img:rounded-xl prose-img:shadow-md">
                        <ReactMarkdown>{content}</ReactMarkdown>
                    </div>
                </div>
            </div>
        </article>
    )
}
